/**
 * A chart the model asked for, drawn as inline SVG.
 *
 * ## Why SVG by hand, and no charting library
 *
 * The same argument `AnswerBody` makes about markdown, and it lands harder here. Recharts, Chart.js
 * and their peers each bring a rendering runtime, a theming layer and a dependency tree into a
 * bank's review, for two chart types over data this platform's own prompts produce. Two shapes,
 * four series, no interaction: that is a hundred lines of arithmetic.
 *
 * It also keeps the property that makes rendering model output safe at all — **nothing here
 * renders HTML.** Every string the model wrote lands as an SVG text node or a React child, never
 * as markup, exactly as in `AnswerBody`.
 *
 * ## The chart is not the data
 *
 * A bar is a shape; the numbers behind it are the answer. So every chart carries a real `<table>`
 * of its own figures, visually hidden and read by screen readers, and the citations the model
 * attached to it are rendered underneath as the same clickable chips the prose uses. An employee
 * who photographs a chart to send to a colleague should be photographing something that names its
 * source — that is golden rule 3 applied to a picture.
 *
 * ## Why the chart measures itself
 *
 * The obvious build is a fixed `viewBox` scaled to the container with `width: 100%`, and it is
 * wrong here in a way only a real browser shows. Measured at 400px: the answer column is ~280px, a
 * 640-unit viewBox scales by 0.32, and an 11-unit axis label renders at **3.5 real pixels**. The
 * chart looked right in the screenshot and was unreadable on the device.
 *
 * So one user unit is one CSS pixel, always: the surface is measured and the viewBox is set to
 * what was measured. Type is then 11px on a phone and 11px on a workstation, which is the only
 * thing an axis label can be. Below `MIN_WIDTH` the figure scrolls instead of shrinking further,
 * the same bargain the answer's tables make.
 *
 * ## Direction
 *
 * In Arabic the categories run right to left, because the chart is read with the sentence around
 * it. Only the *mapping from category index to x position* flips, plus the side the value axis sits
 * on; the arithmetic is otherwise shared, which is the only version of this that stays correct when
 * a third chart type is added.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CitationChip, type Citation, type Direction } from "@nab/shared";

import {
  download,
  fileStem,
  toCsv,
  toPdfBlob,
  toPngBlob,
  type ExportCaption,
} from "./chartExport";

import type { ChartSpec, ChartType } from "./chartSpec";

export interface AnswerChartProps {
  spec: ChartSpec;
  citations: Citation[];
  onCitationClick: (index: number) => void;
  direction: Direction;
}

/** Narrower than this the figure scrolls: three categories and an axis do not fit in less. */
const MIN_WIDTH = 280;
/** Wider than this a chart stops being read and starts being decoration. */
const MAX_WIDTH = 720;
/** Used until the surface has been measured, and in any environment with no ResizeObserver. */
const FALLBACK_WIDTH = 560;

const PAD_TOP = 16;
const PAD_BOTTOM = 52;
/** The value axis's side; the other side only needs room for the last label's overhang. */
const PAD_AXIS = 48;
const PAD_FAR = 12;

/**
 * Series colours, in the order a model's series are given.
 *
 * Blue is identity and orange is action everywhere else in this product, and a chart is the one
 * place both are just "the first two things". Slate and rose follow; four is the cap `chartSpec`
 * enforces, so a fifth series is refused rather than drawn in a colour nobody chose.
 */
const SERIES_COLORS = ["#0042c8", "#ff7e20", "#475569", "#e11d48"] as const;

/**
 * ASCII digits in both languages, deliberately.
 *
 * `Intl.NumberFormat("ar")` renders Arabic-Indic digits and embeds directional marks, which is the
 * problem `shared/src/i18n/timestamp.ts` exists to document: inside an RTL run those marks fight
 * the base direction and a figure comes apart. An axis of numbers has to be scannable next to a
 * table of the same numbers, and the tables render ASCII.
 */
const FIGURES = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/** A round step near `rough` — 1, 2, 5 or 10 times a power of ten. */
function niceStep(rough: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  const step =
    normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
}

interface Scale {
  lo: number;
  hi: number;
  ticks: number[];
}

/**
 * The value axis, and the one rule about it that is not a preference.
 *
 * **A bar chart always includes zero.** A bar's length is its value, so an axis starting at 40
 * makes a 5% difference look like a threefold one — a way of lying with a true number, drawn from
 * the bank's own fee and balance figures. Non-negotiable.
 *
 * **A line chart fits its data.** A line encodes position, not length, and forcing zero is its own
 * distortion in the other direction: measured on a real answer, same-day settlement moving 71% to
 * 86% over six months rendered as a flat line across the top of an empty chart — the trend the
 * chart existed to show, erased. So the range follows the values, padded and snapped to round
 * numbers, and the axis is always labelled so a reader sees where it starts.
 */
function scaleFor(values: number[], type: ChartType): Scale {
  const smallest = Math.min(...values);
  const largest = Math.max(...values);

  let lo: number;
  let hi: number;
  if (type === "bar") {
    lo = Math.min(0, smallest);
    hi = Math.max(0, largest);
  } else {
    // A tenth of the span above and below, so the first and last points are not on the frame.
    const padding = (largest - smallest) / 10;
    lo = smallest - padding;
    hi = largest + padding;
    // A series that never goes negative should not get a negative axis out of the padding.
    if (smallest >= 0 && lo < 0) lo = 0;
  }

  // Every value identical (all zero, typically): give the axis somewhere to go.
  const span = hi - lo || 1;
  const step = niceStep(span / 4);
  const from = Math.floor(lo / step) * step;
  const to = Math.ceil(hi / step) * step;

  const ticks: number[] = [];
  // Counted rather than accumulated, so floating point cannot drop or duplicate the last tick.
  const count = Math.round((to - from) / step);
  for (let index = 0; index <= count; index += 1)
    ticks.push(from + index * step);

  return { lo: from, hi: to === from ? from + step : to, ticks };
}

/** A category label on two lines at most, so a long one does not overlap its neighbour. */
function wrapLabel(label: string): string[] {
  if (label.length <= 14) return [label];
  const words = label.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (current && (current + " " + word).length > 14) {
      lines.push(current);
      current = word;
    } else {
      current = current ? current + " " + word : word;
    }
  }
  if (current) lines.push(current);
  if (lines.length <= 2) return lines;
  return [lines[0]!, lines.slice(1).join(" ").slice(0, 13) + "…"];
}

export function AnswerChart({
  spec,
  citations,
  onCitationClick,
  direction,
}: AnswerChartProps): JSX.Element {
  const surface = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [exportFailed, setExportFailed] = useState(false);
  const [measured, setMeasured] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const node = surface.current;
    // jsdom has no ResizeObserver, and neither does a server render. The fallback width is a
    // readable chart rather than a broken one, which is what a default is for.
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      if (width > 0) setMeasured(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const { t } = useTranslation("app");
  // The citation chip's label lives in `common`, beside the one the prose markers use — the same
  // source opened from a chart and from a sentence must be announced the same way.
  const { t: common } = useTranslation();
  const rtl = direction === "rtl";

  const viewW = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(measured)));
  // Shorter on a phone, where vertical space is the scarce one, and never taller than half as wide.
  const viewH = Math.round(Math.min(300, Math.max(200, viewW * 0.5)));
  const plotW = viewW - PAD_AXIS - PAD_FAR;
  const plotH = viewH - PAD_TOP - PAD_BOTTOM;

  const scale = scaleFor(
    spec.series.flatMap((series) => series.values),
    spec.type,
  );
  const columns = spec.categories.length;
  const slot = plotW / columns;

  /** Category index to the left edge of its slot, flipped in Arabic. */
  const slotStart = (index: number): number =>
    PAD_AXIS + (rtl ? columns - 1 - index : index) * slot;
  const centre = (index: number): number => slotStart(index) + slot / 2;
  const y = (value: number): number =>
    PAD_TOP + (plotH * (scale.hi - value)) / (scale.hi - scale.lo);

  const axisX = rtl ? PAD_AXIS + plotW : PAD_AXIS;
  const zeroY = y(0);

  const known = new Set(citations.map((citation) => citation.index));
  const cited = spec.cite.filter((index) => known.has(index));

  const unitSuffix = spec.unit ? ` ${spec.unit}` : "";

  /**
   * Hand the chart over as a file.
   *
   * CSV needs no canvas and always works. PNG and PDF go through one, which a browser can refuse —
   * a managed policy, a headless context — so the failure is surfaced rather than presenting as a
   * button that does nothing, which is the same reasoning `CopyButton` documents.
   */
  const exportAs = useCallback(
    async (format: "csv" | "png" | "pdf") => {
      const stem = fileStem(spec, t("chart.untitled"));
      try {
        if (format === "csv") {
          const csv = toCsv(spec, {
            title: t("chart.exportTitle"),
            unit: t("chart.exportUnit"),
            category: t("chart.category"),
            sources: t("chart.exportSources"),
          });
          download(
            new Blob([csv], { type: "text/csv;charset=utf-8" }),
            `${stem}.csv`,
          );
          return;
        }
        const svg = svgRef.current;
        if (!svg) {
          // Not a silent return. A missing ref is a wiring bug, and the first version of this
          // *did* have one — the ref was never attached, so both image exports returned here and
          // did nothing at all: no file, no error, no console line. Surfacing it makes the next
          // one visible in the interface instead of only in a download that never arrives.
          throw new Error("the chart has not been rendered yet");
        }
        const caption: ExportCaption = {
          title: spec.title,
          subtitle: spec.unit ? t("chart.unit", { unit: spec.unit }) : "",
          direction,
          fontFamily:
            getComputedStyle(svg).fontFamily || "system-ui, sans-serif",
        };
        if (format === "png") {
          download(await toPngBlob(svg, caption), `${stem}.png`);
        } else {
          download(
            await toPdfBlob(svg, spec.title || t("chart.untitled"), caption),
            `${stem}.pdf`,
          );
        }
      } catch (caught) {
        setExportFailed(true);
        console.warn("chart export failed:", caught);
        window.setTimeout(() => setExportFailed(false), 3000);
      }
    },
    [spec, t, direction],
  );

  return (
    <figure className="mb-4 rounded-[16px] border border-borderSubtle bg-surfaceRaised p-4 shadow-sm">
      {spec.title ? (
        <figcaption className="mb-1 text-heading-sm text-textPrimary">
          <bdi>{spec.title}</bdi>
        </figcaption>
      ) : null}
      {spec.unit ? (
        <p className="mb-3 text-label-md text-textMuted">
          <bdi>{t("chart.unit", { unit: spec.unit })}</bdi>
        </p>
      ) : null}

      {spec.series.length > 1 ? (
        <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1">
          {spec.series.map((series, index) => (
            <li
              key={index}
              className="flex items-center gap-2 text-label-md text-textMuted"
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full"
                style={{
                  background: SERIES_COLORS[index % SERIES_COLORS.length],
                }}
              />
              <bdi>{series.label}</bdi>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Scrolls rather than shrinking below `MIN_WIDTH`, as the answer's tables do. */}
      <div ref={surface} className="overflow-x-auto">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${viewW} ${viewH}`}
          width={viewW}
          height={viewH}
          role="img"
          aria-label={t("chart.label", {
            title: spec.title || t("chart.untitled"),
            count: columns,
          })}
        >
          {/* Gridlines and the value axis. Drawn first so nothing sits on top of a bar. */}
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD_AXIS}
                x2={PAD_AXIS + plotW}
                y1={y(tick)}
                y2={y(tick)}
                stroke={tick === 0 ? "#cbd5e1" : "#e5e7eb"}
                strokeWidth={1}
              />
              <text
                x={rtl ? axisX + 8 : axisX - 8}
                y={y(tick) + 4}
                textAnchor={rtl ? "start" : "end"}
                fontSize={11}
                fill="#64748b"
              >
                {FIGURES.format(tick)}
              </text>
            </g>
          ))}

          {spec.type === "bar"
            ? spec.series.map((series, seriesIndex) => {
                // 60% of the slot is bars, the rest is the gap that separates one category from the
                // next — without it a grouped chart reads as one continuous run of bars.
                const groupWidth = slot * 0.6;
                const barWidth = groupWidth / spec.series.length;
                return (
                  <g
                    key={seriesIndex}
                    fill={SERIES_COLORS[seriesIndex % SERIES_COLORS.length]}
                  >
                    {series.values.map((value, index) => {
                      const x =
                        centre(index) - groupWidth / 2 + seriesIndex * barWidth;
                      const top = Math.min(y(value), zeroY);
                      const height = Math.abs(y(value) - zeroY);
                      return (
                        <rect
                          key={index}
                          x={x}
                          // A zero value would otherwise vanish entirely, which reads as missing
                          // data rather than as a zero.
                          y={height < 1 ? top - 1 : top}
                          width={Math.max(barWidth - 2, 1)}
                          height={Math.max(height, 1)}
                          rx={2}
                        />
                      );
                    })}
                  </g>
                );
              })
            : spec.series.map((series, seriesIndex) => {
                const colour =
                  SERIES_COLORS[seriesIndex % SERIES_COLORS.length];
                const points = series.values
                  .map((value, index) => `${centre(index)},${y(value)}`)
                  .join(" ");
                return (
                  <g key={seriesIndex}>
                    <polyline
                      points={points}
                      fill="none"
                      stroke={colour}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {series.values.map((value, index) => (
                      <circle
                        key={index}
                        cx={centre(index)}
                        cy={y(value)}
                        r={3}
                        fill={colour}
                      />
                    ))}
                  </g>
                );
              })}

          {/* Category labels. Centred, so the surrounding direction cannot pull an Arabic label off
            its own column. */}
          {spec.categories.map((label, index) => (
            <text
              key={index}
              x={centre(index)}
              y={PAD_TOP + plotH + 20}
              textAnchor="middle"
              fontSize={11}
              fill="#64748b"
            >
              {wrapLabel(label).map((line, lineIndex) => (
                <tspan
                  key={lineIndex}
                  x={centre(index)}
                  dy={lineIndex === 0 ? 0 : 13}
                >
                  {line}
                </tspan>
              ))}
            </text>
          ))}
        </svg>
      </div>

      {/* The figures themselves, for anyone not reading the picture. */}
      <table className="nab-visually-hidden">
        <caption>{spec.title || t("chart.untitled")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("chart.category")}</th>
            {spec.series.map((series, index) => (
              <th key={index} scope="col">
                {series.label}
                {unitSuffix}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spec.categories.map((label, index) => (
            <tr key={index}>
              <th scope="row">{label}</th>
              {spec.series.map((series, seriesIndex) => (
                <td key={seriesIndex}>
                  {FIGURES.format(series.values[index]!)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Export. Beside the source rather than in the answer's action row: these produce the
          chart, not the answer, and a row of controls that sometimes means one and sometimes the
          other is a row nobody reads. */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {(["csv", "png", "pdf"] as const).map((format) => (
          <button
            key={format}
            type="button"
            onClick={() => void exportAs(format)}
            aria-label={t(`chart.export.${format}`, {
              title: spec.title || t("chart.untitled"),
            })}
            className="rounded-sm border border-borderSubtle px-2 py-1 text-label-sm font-semibold uppercase tracking-wide text-textMuted transition-colors hover:border-borderStrong hover:text-textPrimary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          >
            {format}
          </button>
        ))}
        {exportFailed ? (
          <span role="status" className="text-label-md text-statusDanger">
            {t("chart.exportFailed")}
          </span>
        ) : null}
      </div>

      {cited.length > 0 ? (
        <p className="mt-3 flex flex-wrap items-center gap-1 text-label-md text-textMuted">
          <span>{t("chart.source")}</span>
          {cited.map((index) => (
            <CitationChip
              key={index}
              index={index}
              onClick={() => onCitationClick(index)}
              label={common("citations.openSource", { index })}
            />
          ))}
        </p>
      ) : null}
    </figure>
  );
}
